import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';

export interface Lesson {
  id: number;
  title: string;
  content: string;
  category: 'uyir' | 'mei' | 'uyirmei' | 'words' | 'sentences' | 'keyboard';
  level: number;
  requiredWPM?: number;
  requiredAccuracy?: number;
}

@Injectable({
  providedIn: 'root'
})
export class LessonService {
  private currentLessonIndex: number = 0;
  
  private lessons: Lesson[] = [

        // Test Uyirmei Eluthu
        
        //  {
        //    id: 0,
        //    title: 'Test Series',
        //    content: 'ககாகிகீகுகூகெகேகைகொகோகௌ',
        //    category: 'keyboard',
        //    level: 0
        //  },

     // Keyboard Mapping Lessons - Level 0
    {
      id: 1,
      title: 'Uyir Keys - Top Row',
      content: 'அஆஇஈ',
      category: 'keyboard',
      level: 0
    },
    {
      id: 2,
      title: 'Uyir Keys - Middle Row',
      content: 'உஊஎஏ',
      category: 'keyboard',
      level: 0
    },
    {
      id: 3,
      title: 'Uyir Keys - Bottom Row',
      content: 'ஐஒஓஔ',
      category: 'keyboard',
      level: 0
    },
    {
      id: 4,
      title: 'Mei Keys - Top Row',
      content: 'க்ச்ட்த்ப்ற்',
      category: 'keyboard',
      level: 0
    },
    {
      id: 5,
      title: 'Mei Keys - Middle Row',
      content: 'ங்ஞ்ண்ந்ம்ன்',
      category: 'keyboard',
      level: 0
    },
    {
      id: 6,
      title: 'Mei Keys - Bottom Row',
      content: 'ய்ர்ல்வ்ழ்ள்',
      category: 'keyboard',
      level: 0
    },

    // Uyir Eluthu (Vowels) - Level 1
    {
      id: 7,
      title: 'Uyir Keys - Vowels Part 1',
      content: 'அஆஇஈஉஊஎஏ',
      category: 'uyir',
      level: 1
    },
    {
      id: 8,
      title: 'Uyir Keys - Vowels Part 2',
      content: 'எஏஐஒஓஔ',
      category: 'uyir',
      level: 1
    },
    {
      id: 9,
      title: 'All Vowels Practice',
      content: 'அஆஇஈஉஊஎஏஐஒஓஔ',
      category: 'uyir',
      level: 1
    },

    // Mei Eluthu (Consonants) - Level 2
    {
      id: 10,
      title: 'க to ங - Consonants Part 1',
      content: 'க்ங்ச்ஞ்ட்',
      category: 'mei',
      level: 2
    },
    {
      id: 11,
      title: 'த to ன - Consonants Part 2',
      content: 'த்ந்ப்ம்ய்',
      category: 'mei',
      level: 2
    },
    {
      id: 12,
      title: 'ர to ன - Consonants Part 3',
      content: 'ர்ல்வ்ழ்ள்ற்ன்',
      category: 'mei',
      level: 2
    },

    // Uyirmei Eluthu (Compound Letters) - Level 3
    {
      id: 13,
      title: 'க Series - Compounds Part 1',
      content: 'ககாகிகீகுகூகெகேகைகொகோகௌ',
      category: 'uyirmei',
      level: 3
    },
    {
      id: 14,
      title: 'ங Series - Compounds Part 2',
      content: 'ஙஙாஙிஙீஙுஙூஙெஙேஙைஙொஙோஙௌ',
      category: 'uyirmei',
      level: 3
    },

    // Common Words - Level 4
    {
      id: 15,
      title: 'Simple Words - Part 1',
      content: 'அம்மா',
      category: 'words',
      level: 4
    },
    {
      id: 16,
      title: 'Simple Words - Part 2',
      content: 'வணக்கம்',
      category: 'words',
      level: 4
    },

    // Sentences - Level 5
    {
      id: 17,
      title: 'Simple Sentences - Part 1',
      content: 'அம்மா அப்பா',
      category: 'sentences',
      level: 5
    },
    {
      id: 18,
      title: 'Simple Sentences - Part 2',
      content: 'தமிழ் வாழ்க',
      category: 'sentences',
      level: 5
    }
  ];

  constructor(private storage: Storage) {
    this.initializeStorage();
  }

  private async initializeStorage() {
    await this.storage.create();
    const savedIndex = await this.storage.get('currentLessonIndex');
    if (savedIndex !== null) {
      this.currentLessonIndex = savedIndex;
    }
  }

  getCurrentLesson(): Lesson {
    return this.lessons[this.currentLessonIndex];
  }

  moveToNextLesson(): boolean {
    if (this.currentLessonIndex < this.lessons.length - 1) {
      this.currentLessonIndex++;
      return true;
    }
    return false;
  }

  moveToPreviousLesson(): boolean {
    if (this.currentLessonIndex > 0) {
      this.currentLessonIndex--;
      return true;
    }
    return false;
  }

  getLessonByIndex(index: number): Lesson | null {
    if (index >= 0 && index < this.lessons.length) {
      return this.lessons[index];
    }
    return null;
  }

  getAllLessons(): Lesson[] {
    return [...this.lessons];
  }

  getLessonsByCategory(category: Lesson['category']): Lesson[] {
    return this.lessons.filter(lesson => lesson.category === category);
  }

  getLessonsByLevel(level: number): Lesson[] {
    return this.lessons.filter(lesson => lesson.level === level);
  }

  setCurrentLesson(index: number): boolean {
    if (index >= 0 && index < this.lessons.length) {
      this.currentLessonIndex = index;
      return true;
    }
    return false;
  }

  async resetProgress() {
    this.currentLessonIndex = 0;
    await this.storage.set('currentLessonIndex', 0);
  }

  getLessonCount(): number {
    return this.lessons.length;
  }

  resetToFirstLesson() {
    this.currentLessonIndex = 0;
    this.storage.set('currentLessonIndex', 0);
  }
} 